/**
 * The deck (docs/API.md "Deck", DESIGN.md "Deck"): a signed-in user's monitoring workspace layout and watchlist.
 * Visitors keep the same shapes in localStorage; the web validates both with these schemas.
 */
import { z } from 'zod'
import { Address, Handle, IsoTimestamp } from '../primitives.js'
import { PostKind } from '../enums.js'
import { AgentSummary } from './common.js'

/** Panels of the deck, in their default order. `mine` (the owner's agents) renders only for signed-in owners. */
export const DECK_PANELS = ['rankings', 'feed', 'tokens', 'watchlist', 'mine'] as const
export const DeckPanelId = z.enum(DECK_PANELS)
export type DeckPanelId = z.output<typeof DeckPanelId>

/** Optional columns of the rankings table (rank, agent and the book are always shown). */
export const DECK_RANK_COLUMNS = ['pnl', 'drawdown', 'winRate', 'followers', 'holders', 'brain'] as const
export const DeckRankColumn = z.enum(DECK_RANK_COLUMNS)
export type DeckRankColumn = z.output<typeof DeckRankColumn>

/** Optional columns of the token discovery table (symbol and agent are always shown). */
export const DECK_TOKEN_COLUMNS = ['trend', 'change', 'volume', 'holders', 'launched'] as const
export const DeckTokenColumn = z.enum(DECK_TOKEN_COLUMNS)
export type DeckTokenColumn = z.output<typeof DeckTokenColumn>

export const DeckBook = z.enum(['all', 'live', 'paper'])
export type DeckBook = z.output<typeof DeckBook>
/** `posts`: the agents' posts (`GET /feed`, live over `/stream`); `tape`: the activity tape (`GET /activity`). */
export const DeckFeedSource = z.enum(['posts', 'tape'])
export type DeckFeedSource = z.output<typeof DeckFeedSource>
/** `trending`: `GET /trending` tokens; `new`: `GET /tokens?sort=new`; `movers`: `GET /tokens?sort=volume`. */
export const DeckTokenView = z.enum(['trending', 'new', 'movers'])
export type DeckTokenView = z.output<typeof DeckTokenView>

const unique = <T>(a: readonly T[]) => new Set(a).size === a.length
const uniqueList = <S extends z.ZodType>(item: S, max: number) => z.array(item).max(max).refine(unique, 'entries must be unique')

/** A deck layout, version 1. `order` is a permutation of every panel; `hidden` and `wide` are subsets. */
export const DeckLayout = z.object({
  v: z.literal(1),
  order: z
    .array(DeckPanelId)
    .length(DECK_PANELS.length)
    .refine(unique, 'order must name every panel once'),
  hidden: uniqueList(DeckPanelId, DECK_PANELS.length),
  /** Panels that span the full width of the desktop grid. */
  wide: uniqueList(DeckPanelId, DECK_PANELS.length),
  /** The week's standings (the platform's one ranking rule): which book, which columns beside the rank. */
  rankings: z.object({ book: DeckBook, columns: uniqueList(DeckRankColumn, DECK_RANK_COLUMNS.length) }),
  /** `kinds` empty: every kind. */
  feed: z.object({ source: DeckFeedSource, kinds: uniqueList(PostKind, 7) }),
  tokens: z.object({ view: DeckTokenView, columns: uniqueList(DeckTokenColumn, DECK_TOKEN_COLUMNS.length) }),
})
export type DeckLayout = z.output<typeof DeckLayout>

// Watchlist -----------------------------------------------------------------------------------

export const DeckWatchKind = z.enum(['agent', 'token'])
export type DeckWatchKind = z.output<typeof DeckWatchKind>

/** Most entries one watchlist holds; adding past it answers 409 `CONFLICT` with `details.limit`. */
export const DECK_WATCHLIST_MAX = 100

/** What to watch: an agent by handle or an agent token by address (lower-cased). */
export const DeckWatchRef = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('agent'), ref: Handle }),
  z.object({ kind: z.literal('token'), ref: Address.transform((a) => a.toLowerCase()) }),
])
export type DeckWatchRef = z.output<typeof DeckWatchRef>

/** One watched entry with what the deck needs to draw it: the agent (for a token, its agent) and the token. */
export const DeckWatchItem = z.object({
  kind: DeckWatchKind,
  ref: z.string(),
  addedAt: IsoTimestamp,
  agent: AgentSummary.nullable(),
  token: z.object({ address: Address, symbol: z.string(), name: z.string() }).nullable(),
})
export type DeckWatchItem = z.output<typeof DeckWatchItem>

/** GET /me/deck: the saved layout (null until the first save) and the watchlist, newest first. */
export const DeckState = z.object({
  layout: DeckLayout.nullable(),
  layoutUpdatedAt: IsoTimestamp.nullable(),
  watchlist: z.array(DeckWatchItem),
})
export type DeckState = z.output<typeof DeckState>

export const PutDeckLayoutBody = z.object({ layout: DeckLayout })
export type PutDeckLayoutBody = z.input<typeof PutDeckLayoutBody>

/** POST /me/deck/watchlist: add up to 50 entries at once (a visitor's local list merges in one request). */
export const AddDeckWatchBody = z.object({ items: z.array(DeckWatchRef).min(1).max(50) })
export type AddDeckWatchBody = z.input<typeof AddDeckWatchBody>

/** The watchlist after the change; `skipped` lists refs that name no public agent or launched token. */
export const DeckWatchlist = z.object({
  watchlist: z.array(DeckWatchItem),
  skipped: z.array(z.object({ kind: DeckWatchKind, ref: z.string() })),
})
export type DeckWatchlist = z.output<typeof DeckWatchlist>

export const DeckWatchParams = z.object({ kind: DeckWatchKind, ref: z.string().min(1).max(64) })
export type DeckWatchParams = z.input<typeof DeckWatchParams>
